import { useCallback } from 'react';
import type { EmbossDirection, EmbossValue } from '../../lib/filters/emboss';
import type { AdjustmentPanelProps } from '../adjustments/registry';
import { RangeRow } from '../adjustments/RangeRow';

/** Emboss panel — amount slider + 4-way direction chips. */
export function EmbossPanel({ value, onChange }: AdjustmentPanelProps<EmbossValue>) {
  const set = useCallback(
    (patch: Partial<EmbossValue>) => onChange({ ...value, ...patch }),
    [onChange, value],
  );
  return (
    <div className="space-y-3">
      <RangeRow
        label="Amount"
        min={0}
        max={300}
        step={1}
        value={value.amount}
        onChange={(v) => set({ amount: v })}
      />
      <DirectionRow value={value.direction} onChange={(d) => set({ direction: d })} />
    </div>
  );
}

function DirectionRow({
  value,
  onChange,
}: {
  value: EmbossDirection;
  onChange: (d: EmbossDirection) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span id="emboss-direction-label" className="text-xs w-16 text-muted-foreground">
        Light
      </span>
      <div role="radiogroup" aria-labelledby="emboss-direction-label" className="flex gap-1 flex-1">
        {(['NE', 'NW', 'SE', 'SW'] as const).map((d) => {
          const active = value === d;
          return (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(d)}
              className={`flex-1 px-2 py-1 text-xs rounded-md border transition-colors ${
                active
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}
